import { detectionKeyOf, FP_RATE_MIN_CLOSED, type DetectionDispositions } from './signal-dispositions'
import { DISPOSITION_LABEL, type Disposition, type Signal } from './signal-model'
import type { PromotedSubgraph } from './signal-subgraphs'

/**
 * AI triage suggestion — DEMO ONLY (docs/signals.md §AI triage). There is no
 * triage-suggestion endpoint (docs/BACKEND-ASKS.md), so demo mode shows a
 * deterministic stand-in and live mode renders nothing. It never applies a
 * disposition: Accept opens the Close dialog prefilled, bulk Apply opens a
 * confirm dialog.
 *
 * The rule, in order:
 *   1. prior handling — when at least FP_RATE_MIN_CLOSED signals from this
 *      detection were closed in the 30d pool and ≥ AGREE_SHARE of them share
 *      one disposition, suggest it at that share;
 *   2. storyline — when the signal sits in a promoted connected-signals
 *      storyline (decision S34), suggest true positive at min(95, 50 + score);
 *   3. otherwise undetermined, with no confidence.
 */

/** Share of closed signals that must agree for the prior-handling branch. */
export const AGREE_SHARE = 0.6
/** Ceiling for the storyline branch's confidence. */
export const STORYLINE_CAP = 95

export type SuggestionBasis = 'prior' | 'storyline' | 'none'

export interface TriageSuggestion {
  disposition: Disposition
  /** 0–100; null for undetermined (nothing to be confident about). */
  confidence: number | null
  basis: SuggestionBasis
  /** Plain-language reasons, built from the facts the rule used. */
  why: string[]
  /** What the rule read: closed signals of this detection, same-entity signals, storyline score. */
  evidence: { priorClosed: number; related: number; storylineScore: number | null }
}

const COUNT_KEYS: [keyof DetectionDispositions, Disposition][] = [
  ['tp', 'true_positive'],
  ['benign', 'benign_true_positive'],
  ['fpLogic', 'false_positive_logic'],
  ['fpData', 'false_positive_data'],
  ['duplicate', 'duplicate'],
  ['undetermined', 'undetermined'],
]

/** The promoted storyline (subgraph) holding the signal — any of its uuids, folded ones too — with the highest score. */
export function bestSubgraph(signal: Pick<Signal, 'uuid'>, subgraphs: readonly PromotedSubgraph[]): PromotedSubgraph | null {
  let best: PromotedSubgraph | null = null
  for (const sg of subgraphs) {
    if (!sg.promoted || !sg.uuids.includes(signal.uuid)) continue
    if (!best || sg.score > best.score) best = sg
  }
  return best
}

export function suggestTriage(input: {
  signal: Signal
  /** Roll-up over the 30d pool (dispositionsByDetection). */
  dispositions: Map<string, DetectionDispositions>
  /** Storylines the job returned (only promoted ones count). */
  subgraphs: readonly PromotedSubgraph[]
  /** Same-entity signals in the pool, for the evidence line. */
  related: number
}): TriageSuggestion {
  const { signal, dispositions, subgraphs, related } = input
  const key = detectionKeyOf(signal)
  const d = key ? (dispositions.get(key) ?? (signal.detectionName ? dispositions.get(signal.detectionName) : undefined)) : undefined
  const story = bestSubgraph(signal, subgraphs)
  const evidence = { priorClosed: d?.closed ?? 0, related, storylineScore: story?.score ?? null }

  if (d && d.closed >= FP_RATE_MIN_CLOSED) {
    let top: { disposition: Disposition; n: number } | null = null
    for (const [k, disp] of COUNT_KEYS) {
      const n = d[k] as number
      if (n > 0 && (!top || n > top.n)) top = { disposition: disp, n }
    }
    if (top && top.n / d.closed >= AGREE_SHARE) {
      const why = [
        `${top.n} of ${d.closed} closed signals from this detection in 30 days were ${DISPOSITION_LABEL[top.disposition].toLowerCase()}`,
      ]
      if (d.lastDisposition) {
        why.push(`Last verdict: ${DISPOSITION_LABEL[d.lastDisposition.disposition].toLowerCase()}${d.lastDisposition.who ? ` by ${d.lastDisposition.who}` : ''}`)
      }
      if (story) why.push(`Also in promoted storyline "${story.title}" (score ${story.score})`)
      return { disposition: top.disposition, confidence: Math.round((top.n / d.closed) * 100), basis: 'prior', why, evidence }
    }
  }

  if (story) {
    return {
      disposition: 'true_positive',
      confidence: Math.min(STORYLINE_CAP, 50 + story.score),
      basis: 'storyline',
      why: [
        `In promoted storyline "${story.title}" (score ${story.score}, ${story.tactics.length} tactic${story.tactics.length === 1 ? '' : 's'})`,
        `${story.uuids.length} signal${story.uuids.length === 1 ? '' : 's'} connected in that storyline`,
        d && d.closed ? `Prior handling is split (${d.closed} closed), so it does not decide` : 'No prior handling for this detection in 30 days',
      ],
      evidence,
    }
  }

  return {
    disposition: 'undetermined',
    confidence: null,
    basis: 'none',
    why: [
      d && d.closed >= FP_RATE_MIN_CLOSED
        ? `Prior handling is split across ${d.closed} closed signals`
        : `Fewer than ${FP_RATE_MIN_CLOSED} closed signals from this detection in 30 days`,
      'Not part of a promoted storyline',
    ],
    evidence,
  }
}

/** Row-hint text: `true positive 91%`, `undetermined`. */
export function suggestionLabel(s: TriageSuggestion): string {
  const label = DISPOSITION_LABEL[s.disposition].toLowerCase().replace(' — ', ' – ')
  return s.confidence === null ? label : `${label} ${s.confidence}%`
}

/** FP suggestions read in the warn tone. */
export function isFpSuggestion(s: TriageSuggestion): boolean {
  return s.disposition === 'false_positive_logic' || s.disposition === 'false_positive_data'
}
